import { FC, useState, useRef, useCallback } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { captureException } from '@sentry/react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { type HTMLMotionProps, motion } from 'motion/react';
import { useNavigate } from 'react-router-dom';
import Confetti from 'react-confetti';
import BaseModal from 'src/components/AriaModal/BaseModal';
import { AriaButton } from 'src/components/Button/Button';
import Input from 'src/components/Input';
import { useI18nContext } from 'src/i18n/i18n-react';
import { trpc } from 'src/lib/trpc';
import CheckCircle from 'src/assets/svgicons/solid/check-circle.svg';
import useNewAnalyticsEvent from 'src/utils/useNewAnalyticsEvent';
import { useUserAndOrganisation } from '../../utils/useUserAndOrganisation';
import { firstStepFields, type OnboardingField } from './constants';
import type { UserOnboardInputV3 } from '@magicbrief/server/src/user/types';

type Props = {
  isNewInvitedUser: boolean;
  onComplete: () => void;
};

export const NewInvitedUserOnboardingModal: FC<Props> = ({
  isNewInvitedUser,
  onComplete,
}) => {
  const navigate = useNavigate();
  const [show, setShow] = useState(true);
  const [step, setStep] = useState(1);
  const modalRef = useRef<HTMLDivElement>(null);
  const direction = useRef<-1 | 0 | 1>(0);
  const userAndOrg = useUserAndOrganisation();
  const onboard = trpc.user.completeUserOnboardFormV3.useMutation();
  const { LL } = useI18nContext();
  const trpcUtils = trpc.useUtils();

  const { recordEvent } = useNewAnalyticsEvent();

  /**
   * @todo get imports working between server and frontend packages
   */
  const userOnboardInput = z.object({
    displayName: z
      .string({ required_error: LL.onboarding.errors.nameRequired() })
      .trim()
      .min(2, { message: LL.onboarding.errors.displayNameTooShort({ n: 2 }) })
      .max(50, {
        message: LL.onboarding.errors.displayNameTooLong({ n: 50 }),
      })
      .regex(/^[a-zA-Z\-'\s]+$/, {
        message: LL.onboarding.errors.invalidCharacters(),
      }),
    workspaceName: z
      .string({ required_error: LL.onboarding.errors.workspaceNameRequired() })
      .trim()
      .min(2, { message: LL.onboarding.errors.workspaceNameTooShort({ n: 2 }) })
      .max(50, {
        message: LL.onboarding.errors.workspaceNameTooLong({ n: 50 }),
      })
      .regex(/^[a-zA-Z0-9\-'\s]+$/, {
        message: LL.onboarding.errors.invalidCharactersWorkspace(),
      }),
  });

  const defaultValues = {
    displayName: '',
    workspaceName:
      userAndOrg.data?.organisation.name === 'My Organisation'
        ? ''
        : (userAndOrg.data?.organisation.name ?? ''),
  };

  const {
    register,
    handleSubmit,
    trigger,
    watch,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<UserOnboardInputV3>({
    defaultValues,
    mode: 'onBlur',
    reValidateMode: 'onChange',
    resolver: zodResolver(userOnboardInput),
  });

  const onSubmit = handleSubmit((data: UserOnboardInputV3) => {
    onboard.mutate(data, {
      onSuccess: () => {
        void recordEvent({
          action: `Submit Successful - New Invited User`,
          target: 'Onboarding',
          metadata: {
            ...data,
          },
        });
        void nextStep();
      },
      onError: (err, opts) => {
        captureException(err, { contexts: { opts } });
      },
    });
  });

  async function finishOnboarding() {
    void recordEvent({
      action: 'Finish Onboarding From Success Screen',
      target: 'Onboarding',
    });
    void trpcUtils.user.getUserAndOrganisation.invalidate();
    void trpcUtils.adBrands.getForYouBrandSuggestions.invalidate();
    setShow(false);
    navigate('insights');
  }

  const isFieldValid = useCallback(
    (field: OnboardingField): boolean => {
      const fieldSchema = userOnboardInput.pick({
        [field]: true,
      } as Record<OnboardingField, true>);

      const parse = fieldSchema.safeParse({
        [field]: getValues(field),
      });

      return parse.success;
    },
    [getValues, userOnboardInput]
  );

  async function nextStep() {
    const fieldsToValidate = firstStepFields;

    const isValid = await trigger(fieldsToValidate, { shouldFocus: true });

    if (isValid) {
      direction.current = 1;
      setStep(step + 1);
    }
  }

  const hasEditPermission =
    userAndOrg.data?.role != null &&
    ['wizard', 'superuser', 'owner', 'admin', 'editor'].includes(
      userAndOrg.data.role
    );

  const stepAnimationProps: HTMLMotionProps<'div'> = {
    initial: { x: direction.current * 30, opacity: 0 },
    animate: { x: 0, opacity: 1 },
    transition: {
      duration: 0.2,
      bounce: 0,
    },
  };

  watch();

  return (
    <BaseModal
      ref={modalRef}
      show={show}
      onClose={null}
      afterClose={onComplete}
      modalClassName="bg-white rounded-lg sm:w-auto sm:m-0 text-primary"
      overlayClassName="overflow-y-hidden"
      dialogClassName="w-full sm:w-[478px] p-6 flex flex-col gap-6 relative overflow-y-auto"
    >
      <form onSubmit={onSubmit}>
        {/* Step 1 */}
        {step === 1 && (
          <motion.div {...stepAnimationProps} className="space-y-6">
            <div className="text-center">
              <h1 className="text-pretty text-lg font-semibold leading-8">
                {LL.onboarding.step1Title()}
              </h1>
              <p className="text-sm text-[#917DD2]">
                {LL.onboarding.step1Description()}
              </p>
            </div>
            <div className="space-y-4">
              <Input
                {...register('displayName')}
                error={errors.displayName?.message}
                disabled={isSubmitting}
                label={LL.onboarding.displayName.label()}
                placeholder={LL.onboarding.displayName.placeholder()}
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
              />
              <Input
                {...register('workspaceName')}
                error={errors.workspaceName?.message}
                disabled={
                  !hasEditPermission || isSubmitting || isNewInvitedUser
                }
                label={LL.onboarding.workspaceName.label()}
                placeholder={LL.onboarding.workspaceName.placeholder()}
              />
            </div>
            <AriaButton
              htmlType="submit"
              className="mt-4 w-full"
              isDisabled={
                !firstStepFields.every((field) => isFieldValid(field))
              }
              loading={isSubmitting || onboard.isLoading}
            >
              {LL.continue()}
            </AriaButton>
          </motion.div>
        )}

        {step === 2 && (
          <motion.div
            {...stepAnimationProps}
            className="space-y-4 px-6 py-8 text-center"
          >
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="mx-auto w-fit rounded-full bg-green-500/10 p-1.5"
            >
              <CheckCircle className="size-9 text-green-500" />
            </motion.div>
            <div>
              <h1 className="text-pretty text-2xl font-semibold leading-8">
                {LL.onboarding.success.setupComplete()}
              </h1>
              <p className="text-sm text-primary/70">
                {LL.onboarding.success.setupCompleteDescription()}
              </p>
            </div>
            <AriaButton onPress={finishOnboarding} className="!mt-10 w-full">
              {LL.getStarted()}
            </AriaButton>
          </motion.div>
        )}
      </form>

      {step === 2 && (
        <Confetti
          width={modalRef.current?.clientWidth}
          height={modalRef.current?.clientHeight}
          gravity={0.2}
          recycle={false}
          numberOfPieces={500}
        />
      )}
    </BaseModal>
  );
};
